/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Card, Checkbox, Typography } from '@mui/material';
import { useContext, type ReactNode } from 'react';

import { conflictInside, premiumFeatureTooltip, allFeaturesDisabled } from '../../../../constants/tooltips';
import { HighlightSearch } from '../../../HighlightSearch/HighlightSearch';
import { PluginPremiumBadge, PluginDisabledBadge, PluginConflictBadge } from '../../../PluginBadges/PluginBadges';
import { LabelledSwitch } from '../../../LabelledSwitch/LabelledSwitch';
import type { CapabilityItemProps } from '../types';
import type { CapabilityParsed } from '../../types';
import { GTMContext } from '../../../../contexts/GTMContext';
import { GTM_EVENT_GROUP_TOGGLE } from '../../../../constants/GTMEventNames';
import { Label } from '../../../Label/Label';
import { kebabCase } from 'lodash';

export function CapabilityItem( {
	capability,
	expandText,
	isExpanded,
	onPluginSelectionChange,
	onGroupToggle,
	onCardClick
}: CapabilityItemProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	const opacity = capability.isDisabled ? 0.5 : 1;

	function handleLabelledSwitchToggle( capability: CapabilityParsed ) {
		onGroupToggle( capability );

		triggerGTMEvent( {
			event: GTM_EVENT_GROUP_TOGGLE,
			group: capability.name,
			view: 'basic',
			active: !capability.toggleValue
		} );
	}

	return (
		<Card
			sx={ {
				position: 'relative',
				padding: '10px 10px 32px',
				margin: '-1px',
				cursor: 'pointer',
				borderColor: isExpanded ? 'primary.main' : 'inherit'
			} }
			data-testid={ `card-description-${ kebabCase( capability.name ) }` }
			onClick={ onCardClick }
		>
			<Box sx={ { display: 'flex', justifyContent: 'space-between' } }>
				<Box>
					<Box sx={ { display: 'flex', gap: '5px', alignItems: 'baseline', maxWidth: 'calc(100% - 32px)' } }>
						<Typography
							color={ 'text.primary' }
							fontSize={ '14px' }
							fontWeight={ 700 }
							lineHeight={ '24px' }
							sx={ {
								cursor: 'pointer',
								opacity
							} }
						>
							<HighlightSearch>
								{ capability.name }
							</HighlightSearch>
						</Typography>
						<Box display='flex' flexWrap='wrap' gap='5px'>
							{ capability.isPremium && <PluginPremiumBadge tooltipTitle={ premiumFeatureTooltip }/> }
							{ capability.isDisabled && <PluginDisabledBadge tooltipTitle={ allFeaturesDisabled }/> }
							{ capability.hasConflict && !capability.isDisabled && <PluginConflictBadge tooltipTitle={ conflictInside }/> }
						</Box>
					</Box>
					<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' } sx={ { opacity } }>
						<HighlightSearch>
							{ capability.description }
						</HighlightSearch>
					</Typography>
				</Box>
				<Box
					sx={ {
						display: 'flex',
						flexFlow: 'column',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						position: 'absolute',
						right: '10px',
						height: 'calc(100% - 20px)'
					} }
				>
					<Box display='flex' gap='8px' alignItems='center' onClick={ e => e.stopPropagation() }>
						{ capability.hasToggle && (
							<LabelledSwitch
								id={ `capability-toggle-${ capability.id }` }
								text={ capability.toggleLabel! }
								active={ capability.toggleValue! }
								onToggle={ () => handleLabelledSwitchToggle( capability ) }
								width='65px'
							/>
						) }
						{ capability.isVirtual ? (
							<Label text='active'/>
						) : (
							<Checkbox
								id={ 'capability-' + capability.id }
								data-testid='toggle-capability-checkbox'
								checked={ capability.isSelected }
								disabled={ capability.isDisabled }
								indeterminate={ capability.isSelectedIndeterminate }
								onChange={ () => onPluginSelectionChange( capability ) }
							/>
						) }
					</Box>

					<Typography fontSize='x-small' color='text.primary' display='flex' alignItems='center'>
						{ expandText }
						{ isExpanded ?
							<ExpandLess sx={ { width: '16px', height: '16px' } }/> :
							<ExpandMore sx={ { width: '16px', height: '16px' } }/> }
					</Typography>
				</Box>
			</Box>
		</Card>
	);
}
